<template>
  <section
    :class="
      bem({
        'theme-promo': hasPromoTheme,
        horizontalAlignment: block.horizontalAlignment,
        noDesktopImages: hasNoDesktopImages,
        noMobileImage: !block.mobileImage,
        hasDesktopHeightSet: !!block.desktopMinHeight && hasNoDesktopImages,
        hasMobileHeightSet: !!block.mobileMinHeight && block.mobileImage,
        foregroundImagesPosition: block.foregroundImagesDesktopPosition,
        isNotFirstRow: rowPosition > 0,
        variation: block.variation
      })
    "
  >
    <component
      :is="
        block.backgroundMediaLink && !block.backgroundMediaOverlay
          ? VisionLink
          : 'div'
      "
      :class="bem('bg')"
      :to="
        block.backgroundMediaLink && !block.backgroundMediaOverlay
          ? buildInternalLink(block.backgroundMediaLink)
          : null
      "
      @click="block.backgroundMediaLink && !block.backgroundMediaOverlay ? handleLinkClick('Background Media') : null"
    >
      <div
        v-if="
          block.backgroundVideo &&
            block.backgroundVideo.video?.video?.muxPlaybackId
        "
        :class="bem('bg-video')"
      >
        <BaseVideo
          :video="block.backgroundVideo.video"
          :preview-start-time="0"
          :will-loop="true"
          :bg-video="true"
          :full-width="false"
        />
      </div>
      <DatoCmsImage
        v-else-if="block.backgroundImage"
        :data="block.backgroundImage.responsiveImage"
        object-fit="cover"
        :object-position="`${block.backgroundImage.focalPoint.x * 100}% ${
          block.backgroundImage.focalPoint.y * 100
        }%`"
        layout="fill"
        :priority="rowPosition === 0"
        sizes="100vw"
      />
      <component
        :is="
          block.backgroundMediaLink && block.backgroundMediaOverlay
            ? VisionLink
            : 'div'
        "
        v-if="props.block.backgroundMediaOverlay"
        :class="bem('bg-overlay')"
        :to="buildInternalLink(block.backgroundMediaLink)"
        @click="block.backgroundMediaLink && block.backgroundMediaOverlay ? handleLinkClick('Background Media') : null"
      />
    </component>
    <div :class="bem('content-wrapper')">
      <component
        :is="block.desktopImage1Link ? VisionLink : 'div'"
        v-if="block.desktopImage1"
        :to="buildInternalLink(block.desktopImage1Link)"
        :class="
          bem('fg-img', {
            one: true,
            desktop: true,
          })
        "
        @click="handleLinkClick('Foreground Media 1')"
      >
        <DatoCmsImage
          :data="block.desktopImage1.responsiveImage"
          object-fit="cover"
          :object-position="`${block.desktopImage1.focalPoint.x * 100}% ${
            block.desktopImage1.focalPoint.y * 100
          }%`"
          layout="fill"
          :priority="rowPosition === 0"
          sizes="30vw"
        />
      </component>
      <component
        :is="block.desktopImage2Link ? VisionLink : 'div'"
        v-if="block.desktopImage2"
        :to="buildInternalLink(block.desktopImage2Link)"
        :class="
          bem('fg-img', {
            two: true,
            desktop: true,
          })
        "
        @click="handleLinkClick('Foreground Media 2')"
      >
        <DatoCmsImage
          :data="block.desktopImage2.responsiveImage"
          object-fit="cover"
          :object-position="`${block.desktopImage2.focalPoint.x * 100}% ${
            block.desktopImage2.focalPoint.y * 100
          }%`"
          layout="fill"
          :priority="rowPosition === 0"
          sizes="30vw"
        />
      </component>
      <component
        :is="block.mobileImageLink ? VisionLink : 'div'"
        v-if="block.mobileImage"
        :to="buildInternalLink(block.mobileImageLink)"
        :class="
          bem('fg-img', {
            mobile: true,
          })
        "
        @click="handleLinkClick('Foreground Media Mobile')"
      >
        <DatoCmsImage
          :data="block.mobileImage.responsiveImage"
          object-fit="cover"
          :object-position="`${block.mobileImage.focalPoint.x * 100}% ${
            block.mobileImage.focalPoint.y * 100
          }%`"
          layout="fill"
          :priority="rowPosition === 0"
        />
      </component>
      <div :class="bem('content')">
        <GenericStructuredText
          :class="bem('generic-content')"
          :content="block.content"
          copy-size="body-sm"
          cta-size="small"
          :theme="hasPromoTheme ? 'promo' : 'default'"
          :max-heading-level="6"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
import { Image as DatoCmsImage } from 'vue-datocms'
import { VisionLink } from '#components'

const bem = useBEM('MediaHeader')
const promoStore = usePromoStore()

const { buildInternalLink } = useUrlBuilder()
const { $track } = useNuxtApp()

const props = defineProps({
  block: {
    type: Object,
    required: true,
  },
  rowPosition: {
    type: Number,
    default: 0,
  },
})

const minDesktopHeight = `${props.block.desktopMinHeight ?? 50}` + 'dvh'
const minMobileHeight = `${props.block.mobileMinHeight ?? 70}` + 'dvh'
const backgroundOverlayOpacity = `${
  (props.block.backgroundMediaOverlay ?? 0) / 100
}`

const hasNoDesktopImages = computed(() => {
  return !props.block.desktopImage1 && !props.block.desktopImage2
})

const hasPromoTheme = computed(() => {
  return (promoStore.theme?.id && promoStore.theme?.id === props.block.theme?.id)
})

async function handleLinkClick(elementId) {
  await $track.global.linkClicked({
    interactionElement: elementId,
    interactionSection: props.block.trackingId,
    interactionCategory: 'Media Header',
    position: props.rowPosition + 1,
  }, 'Clicked Media Header ' + elementId)
}
</script>

<style lang="postcss">
.MediaHeader {
  @apply relative w-full mb-24;
}

.MediaHeader__content-wrapper {
  @apply flex flex-col sm:flex-row
    gap-y-4 sm:gap-y-0
    gap-x-8
    items-center sm:items-start
    justify-center
    pt-[var(--nav-height)] sm:pt-[calc(var(--nav-height)+32px)]
    relative
    w-full h-full
    pointer-events-none;

  padding-left: clamp(24px, 4vw, 72px);
  padding-right: clamp(24px, 4vw, 72px);

  @screen xxl {
    @apply gap-16;
  }
}

.MediaHeader__bg {
  @apply w-full h-full absolute;
}

.MediaHeader__bg-overlay {
  @apply absolute bg-off-black w-full h-full top-0 left-0 opacity-[v-bind('backgroundOverlayOpacity')];
}

.MediaHeader__bg-video {
  @apply w-full h-full overflow-hidden relative z-0;
}

.MediaHeader__fg-img {
  @apply relative aspect-square w-1/2 rounded-md overflow-hidden
    sm:mt-[calc(10%-var(--nav-height))] lg:mt-[calc(8%-var(--nav-height))] xxl:mt-[calc(6%-var(--nav-height))]
    translate-y-[10%] sm:translate-y-[5%] pointer-events-auto;
}

.MediaHeader__fg-img--desktop {
  @apply hidden lg:block shrink-0
    w-[30%]
    max-w-xs sm:max-w-none;
}

.MediaHeader__fg-img--mobile {
  @apply block lg:hidden
    w-full sm:w-1/2
    order-2 sm:order-1
    -mt-[5%] sm:mt-0;
}

.MediaHeader__content {
  @apply w-full md:w-auto
    order-1 sm:order-2
    mt-8 smMax:mb-2 sm:my-auto
    pointer-events-auto;
}

.MediaHeader__generic-content {
  h1 {
    @apply mb-3 md:mb-4 xxl:mb-4;
  }

  .GenericStructuredText__cta {
    @apply mt-5;
  }
}

/**

MODIFIERS

--horizontal-alignment-{left, center, right}
--no-desktop-images
--has-desktop-height-set
--has-mobile-height-set
--has-no-mobile-image
--foreground-image-position-{left, right}
--is-not-first-row
--variation-{default, compact}

*/
.MediaHeader--horizontal-alignment-center {
  .MediaHeader__content-wrapper {
    @apply smMax:items-center sm:justify-center;
  }
}

.MediaHeader--horizontal-alignment-left {
  .MediaHeader__content-wrapper {
    @apply smMax:items-start sm:justify-start;
  }
}

.MediaHeader--horizontal-alignment-right {
  .MediaHeader__content-wrapper {
    @apply smMax:items-end sm:justify-end;
  }
}

.MediaHeader--no-desktop-images {
  @apply lg:min-h-[70dvh];

  .MediaHeader__content-wrapper {
    @apply lg:pt-4;
  }
}

.MediaHeader--no-mobile-image {
  .MediaHeader__content-wrapper {
    @apply smMax:pb-[var(--nav-height)] smMax:pt-[calc(var(--nav-height)+12px)];
  }

  .MediaHeader__content {
    @apply smMax:mt-0;
  }
}

.MediaHeader--has-desktop-height-set {
  min-height: v-bind('minMobileHeight');

  @screen lg {
    min-height: v-bind('minDesktopHeight');
  }
}

.MediaHeader--has-mobile-height-set {
  min-height: v-bind('minMobileHeight');

  @screen lg {
    min-height: v-bind('minDesktopHeight');
  }
}

.MediaHeader--foreground-images-position-right {
  .MediaHeader__fg-img {
    order: 2;
  }

  .MediaHeader__content {
    order: 1;
  }
}

.MediaHeader--is-not-first-row {
  .MediaHeader__content-wrapper {
    @apply pt-8 sm:pt-16;
  }
}

.MediaHeader--variation-compact {
  .MediaHeader__content-wrapper {
    @apply pt-8;
  }
}
</style>
